import type { ReactNode } from "react";
import { genres, statusOptions, sortOptions } from "@/mocks/comics";

interface FilterPanelProps {
  selectedGenres: string[];
  status: string;
  sort: string;
  onToggleGenre: (genre: string) => void;
  onStatusChange: (status: string) => void;
  onSortChange: (sort: string) => void;
  onReset: () => void;
}

function FilterGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-6 last:mb-0">
      <h3 className="font-heading font-semibold text-sm text-foreground-950 mb-3 flex items-center gap-2">
        <span className="w-1 h-4 rounded-full bg-primary-500" />
        {title}
      </h3>
      {children}
    </div>
  );
}

export default function FilterPanel({
  selectedGenres,
  status,
  sort,
  onToggleGenre,
  onStatusChange,
  onSortChange,
  onReset,
}: FilterPanelProps) {
  return (
    <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-bold text-foreground-950 flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-500" />
          筛选
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" />
          重置
        </button>
      </div>

      <FilterGroup title="题材">
        <div className="flex flex-wrap gap-2">
          {genres.map((genre) => {
            const active = selectedGenres.includes(genre);
            return (
              <button
                key={genre}
                type="button"
                onClick={() => onToggleGenre(genre)}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-400"
                }`}
              >
                {genre}
              </button>
            );
          })}
        </div>
      </FilterGroup>

      <FilterGroup title="连载状态">
        <div className="flex flex-col gap-2">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onStatusChange(option.value)}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors cursor-pointer text-left ${
                status === option.value
                  ? "bg-secondary-100 text-foreground-950"
                  : "text-foreground-700 hover:bg-background-200"
              }`}
            >
              <span
                className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                  status === option.value ? "border-primary-500" : "border-background-300"
                }`}
              >
                {status === option.value && <span className="w-2 h-2 rounded-full bg-primary-500" />}
              </span>
              {option.label}
            </button>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title="排序方式">
        <div className="flex flex-col gap-2">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onSortChange(option.value)}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors cursor-pointer text-left ${
                sort === option.value
                  ? "bg-secondary-100 text-foreground-950"
                  : "text-foreground-700 hover:bg-background-200"
              }`}
            >
              <span
                className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                  sort === option.value ? "border-accent-500" : "border-background-300"
                }`}
              >
                {sort === option.value && <span className="w-2 h-2 rounded-full bg-accent-500" />}
              </span>
              {option.label}
            </button>
          ))}
        </div>
      </FilterGroup>
    </div>
  );
}